import INTOUCH_API from "../../services/Api";
import { useContext, useEffect, useState } from "react";
import Game from "../../store/Game";
import useCurrentGame from "../../hooks/useCurrentGame";
import useCustomNavigate from "../../hooks/useCustomNavigate";
import useCurrentUserData from "../../hooks/useCurrentUserData";
import { useLocation, useNavigate } from "react-router-dom";
import useBrowserStorage from "../../hooks/useBrowserStorage";
import {
  INSIGHT_INSTRUCTION_READ,
  TEMPORARY_ID_KEY,
} from "../../utils/constants";
import useAuth from "../../hooks/useAuth";
import ROUTES from "../../utils/routes.js";
import useModalActions from "../../hooks/useModalActions";
import { SOCKET_EVENTS, SocketContext } from "../../components/SocketProvider";
import { MODAL_TYPES } from "../../utils/emitter";
import { askToInvitePartner, findGame } from "../../hooks/useCreateBlankForm";
import trackEvent from "../../utils/analytics";

export default function useController(countCardRef) {
  const [isInsightLoading, setIsInsightLoading] = useState(false);
  const [isCardsLoading, setIsCardsLoading] = useState(false);
  const { openModal } = useModalActions();
  const { getCurrentGame } = useCurrentGame();
  const { getItem } = useBrowserStorage();
  const { token } = useAuth();
  const { navigateTo } = useCustomNavigate();
  const { getUserData } = useCurrentUserData();
  const location = useLocation();
  const navigate = useNavigate();
  const { sendEvent } = useContext(SocketContext);

  const isIos = /iPhone/.test(navigator.userAgent);

  // Набор карт: базовые 88 или +57 (вход «Начать тест» передаёт setType в state)
  const setType =
    location.state?.setType === "EXTENDED_57" ? "EXTENDED_57" : null;

  const getCards = async () => {
    // перезагружаем колоду, если загружен ДРУГОЙ набор (например, ЛК оставил базу,
    // а мы входим в +57 по попапу «партнёр разблокировал») — иначе свайпер получит
    // старые 88 отвеченных карт и мгновенно завершит «игру»
    if (Game.cards.length && Game.cardsSetType === (setType || "BASE")) return;
    await Game.getCards("questions_cards", setType ? { setType } : null);
  };

  const handleEndGame = () => {
    const user = getUserData();
    if (user && user.id && Game.game?.id) {
      console.log("🎮 Отправляем END_GAME event");
      sendEvent(SOCKET_EVENTS.END_GAME, {
        senderId: user.id,
        room: Game.game.id,
        isEnd: true,
        // item 14: сообщаем бэку, какой набор доигран. Для +57 бэк НЕ шлёт
        // повторное «смотри результаты», пока партнёр не пройдёт свои +57.
        setType: setType || "BASE",
      });
    }
    // Раскидал +57, но набор не разблокирован — попап оплаты
    // «Купить 29$» / «2900 баллов» (ТЗ п.7.5.2); модалка глобальная и переживёт навигацию
    if (setType === "EXTENDED_57" && Game.game && !Game.game.hasExtendedSet) {
      openModal(MODAL_TYPES.BUY_PRODUCT, {
        productType: "EXTENDED_57",
        gameId: Game.game.id,
      });
    }
    // justCompleted: только этот путь — фактическое доигрывание колоды; входы в уже
    // завершённую игру (checkEndGameOnInit и др.) game_complete слать не должны
    navigate(ROUTES.END_GAME_PAGE, {
      state: { justCompleted: true, gameId: Game.game?.id, setType: setType || "BASE" },
    });
  };

  const checkEndGame = () => {
    setTimeout(() => {
      const countCard = countCardRef.current;
      if (
        countCard >= Game.cards.length &&
        countCard !== 0 &&
        Game.cards.length !== 0
      ) {
        handleEndGame();
      }
    }, 1000);
  };

  const checkEndGameOnInit = () => {
    const totalCards = Game.cards.length;
    const history = Game.gameHistory || [];

    if (!totalCards || !history.length) {
      return;
    }

    // считаем отвеченными только карты ТЕКУЩЕЙ колоды: история игры содержит
    // ответы всех наборов, и без фильтра 88 базовых ответов мгновенно
    // «завершали» свежекупленный набор +57 (88 >= 57)
    const deckIds = new Set(Game.cards.map((card) => card.id));
    // item 2: гейт «уже пройдено» по РАЗНЫМ questionId (как бэк), а не по длине —
    // дубль/DELETED-ответ не должен ложно завершать игру при реальном пропуске карты
    const answeredDistinct = new Set(
      history
        .filter((card) => card.answer > 0 && deckIds.has(card.questionId))
        .map((card) => card.questionId)
    ).size;

    if (answeredDistinct >= totalCards) {
      // handleEndGame();
      navigate(ROUTES.END_GAME_PAGE);
    }
  };

  const onFeedbackSwipe = async (answerInfo) => {
    const game = getCurrentGame();
    const currentUser = getUserData();
    const temporaryUserId = getItem(TEMPORARY_ID_KEY);
    const answerData = {
      gameId: game.id,
      answer: answerInfo.answer,
      questionId: answerInfo.id,
    };
    if (token) {
      answerData.userId = currentUser.id;
    } else {
      answerData.temporaryUserId = temporaryUserId;
    }
    // item 2: сохраняем ответ НАДЁЖНО — при сбое сети/5xx повторяем (до 3 попыток).
    // Потерянный POST оставлял дыру в наборе, из-за чего игра зависала на 87/88.
    // Повтор безопасен: бэк идемпотентен (upsert по gameId+userId+questionId).
    const send = (attempt = 0) => {
      INTOUCH_API.requestManager(
        "answers",
        answerData,
        () => {
          // Проверяем конец игры после успешной отправки ответа
          checkEndGame();
        },
        (error) => {
          console.error("❌ Ошибка отправки ответа:", error);
          if (attempt < 2) {
            setTimeout(() => send(attempt + 1), 800 * (attempt + 1));
          }
        }
      );
    };
    send();
  };

  const createInsightForm = (card) => {
    const instructionRead = getItem(INSIGHT_INSTRUCTION_READ);
    setIsInsightLoading(true);
    const game = getCurrentGame();
    const data = {
      gameId: game.id,
      questionId: card.id,
    };
    INTOUCH_API.requestManager(
      "createInsightForm",
      data,
      (response) => {
        if (!instructionRead) {
          navigateTo(ROUTES.INSIGHT_INSTRUCTION, {
            state: { formId: response.data.id },
          });
        } else {
          navigateTo(ROUTES.INSIGHT_QUESTIONNAIRE_FORM_PAGE(response.data.id), {
            state: {
              formId: response.data.id,
              prevPath: location.pathname,
              from: location.pathname,
            },
          });
        }
      },
      (err) => {
        if (err.status === 400) {
          INTOUCH_API.requestManager(
            "getInsightFormPairByQuestionId",
            { payload: { questionId: card.id, gameId: game.id } },
            (response) => {
              const currentUser = getUserData();
              const foundInsightForm = response.data.find(
                (form) => form.userId === currentUser.id
              );
              // 400 мог прийти как gameNotFound (игра без партнёра), а не только
              // «анкета уже создана» — тогда пары нет и .id читать нельзя.
              // 07.10: раньше здесь молча выходили — значок анкеты «не нажимался».
              if (!foundInsightForm) {
                askToInvitePartner(findGame(game.id) || game, openModal);
                return;
              }
              if (!instructionRead) {
                navigateTo(ROUTES.INSIGHT_INSTRUCTION, {
                  state: { formId: foundInsightForm.id },
                });
              } else {
                navigateTo(
                  ROUTES.INSIGHT_QUESTIONNAIRE_FORM_PAGE(foundInsightForm.id),
                  {
                    state: {
                      formId: foundInsightForm.id,
                      prevPath: location.pathname,
                      from: location.pathname,
                    },
                  }
                );
              }
            },
            () => {},
            () => setIsInsightLoading(false)
          );
        }
      },
      () => {
        setIsInsightLoading(false);
      }
    );
  };

  useEffect(() => {
    // GA4 «Stats v2»: юзер начал игру — вход в свайпер (покрывает все кнопки
    // «Начать тест»: лендинг, ЛК, попапы; set_type различает базу и +57).
    // Дедуп на игру+набор в рамках вкладки: ремаунты свайпера (выход в ЛК и
    // обратно, hot-переходы) не должны множить game_start.
    const gaKey = `ga_start_${
      location.state?.gameId ?? getCurrentGame()?.id ?? "na"
    }_${setType || "BASE"}`;
    if (!sessionStorage.getItem(gaKey)) {
      sessionStorage.setItem(gaKey, "1");
      trackEvent("game_start", {
        event_category: "Game Flow",
        game_name: "Wepurplay Test",
        set_type: setType || "BASE",
      });
    }
    setIsCardsLoading(true);
    const rootContainer = document.getElementsByTagName("body");
    if (rootContainer.length) {
      rootContainer[0].style.setProperty("overflow", "hidden");
      rootContainer[0].style.setProperty(
        "height",
        `${isIos ? "auto" : "100dvh"}`
      );
    }
    getCards()
      .then(async () => {
        const temporaryUserId = getItem(TEMPORARY_ID_KEY);
        const currentUser = getUserData();
        const game = getCurrentGame();
        const data = {
          gameId: game.id,
        };
        if (token) {
          data.userId = currentUser.id;
        } else {
          data.temporaryUserId = temporaryUserId;
        }
        await Game.getGameHistory(data);
        if (token) {
          await Game.checkLastGameAnswers(data);
        }
      })
      .finally(() => {
        setIsCardsLoading(false);
        // При заходе на страницу один раз проверяем, не закончена ли уже игра
        checkEndGameOnInit();
      });
    return () => {
      Game.removeCards();
      rootContainer[0].style.setProperty("overflow", "initial");
      rootContainer[0].style.setProperty("height", "100%");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return {
    onFeedbackSwipe,
    isInsightLoading,
    createInsightForm,
    isCardsLoading,
  };
}
